import { useStartSuccessFeeMigration } from '@/hooks/actions/use-start-success-fee-migration';
import { useIsBrandManager } from '@/hooks/use-is-brand-manager';
import type { SuccessFeeMigrationStatus } from '@fluenceur/types';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import SettingsSection from '../SettingsSection';
import { ApprovalPendingState } from './ApprovalPendingState';
import { AwaitingCycleState } from './AwaitingCycleState';
import { BlockedState } from './BlockedState';
import { MigrationStateLoading } from './MigrationStateLayout';
import { RetryableState } from './RetryableState';
import { startBlockedReasonKey } from './start-gate';
import { TermsAcceptedState } from './TermsAcceptedState';

/**
 * States the section stays silent on: `to_invite` names a change with nothing to
 * do about it (the banner and the acceptance modal own that moment), and
 * `active` is the normal state of the world once the cutover is recorded.
 */
const SILENT_STATES: ReadonlySet<SuccessFeeMigrationStatus['state']> = new Set([
  'to_invite',
  'active',
]);

/**
 * Whether the billing page shows the success-fee section at all.
 *
 * The page reads it twice: once to render the section, once to decide whether
 * the plan section is the last one. Both answers must come from here so they
 * cannot drift apart.
 */
export const shouldRenderSuccessFeeMigration = (
  status: SuccessFeeMigrationStatus | null | undefined
): status is SuccessFeeMigrationStatus =>
  status !== null && status !== undefined && !SILENT_STATES.has(status.state);

interface SuccessFeeMigrationCardProps {
  status: SuccessFeeMigrationStatus;
  /** True while the post-Shopify-return refetch is in flight. */
  isRefetching?: boolean;
}

/**
 * The success-fee billing migration row of the billing settings page.
 *
 * Its title never changes; only the body sentence and the action do. The call to
 * action exists for brand owners and admins only, everyone else reads the
 * sentence that says who can act.
 */
export const SuccessFeeMigrationCard: React.FC<SuccessFeeMigrationCardProps> = ({
  status,
  isRefetching = false,
}) => {
  const { t } = useTranslation();
  const { canManage } = useIsBrandManager();
  const startMigration = useStartSuccessFeeMigration();

  const isStarting = startMigration.isPending;
  const start = () => startMigration.mutate();

  // The approval page already exists, so resuming is a plain redirect. A pending
  // attempt past the 48h window no longer carries one, and a fresh attempt is
  // the only way forward.
  const resume = () => {
    if (status.confirmationUrl) {
      window.location.href = status.confirmationUrl;
      return;
    }
    start();
  };

  // A viewer already reads a sentence saying who can act, so the API's reason
  // only ever replaces an admin's button.
  const blockedReasonKey = canManage ? startBlockedReasonKey(status) : null;

  const currencyNotice = status.shopCurrencyDiffersFromBilling
    ? t('settings.subscription.successFeeMigration.currencyNotice')
    : undefined;

  const body = () => {
    if (isRefetching) {
      return (
        <MigrationStateLoading label={t('settings.subscription.successFeeMigration.checking')} />
      );
    }

    switch (status.state) {
      case 'terms_accepted':
        return (
          <TermsAcceptedState
            isAdmin={canManage}
            blockedReasonKey={blockedReasonKey}
            isStarting={isStarting}
            onStart={start}
            currencyNotice={currencyNotice}
          />
        );
      case 'shopify_approval_pending':
        return (
          <ApprovalPendingState
            isAdmin={canManage}
            isStarting={isStarting}
            onResume={resume}
            currencyNotice={currencyNotice}
          />
        );
      case 'approved_awaiting_cycle':
        return (
          <AwaitingCycleState
            expectedEffectiveAt={status.expectedEffectiveAt}
            currencyNotice={currencyNotice}
          />
        );
      case 'declined':
      case 'expired':
        return (
          <RetryableState
            reason={status.state}
            isAdmin={canManage}
            blockedReasonKey={blockedReasonKey}
            isStarting={isStarting}
            onStart={start}
            currencyNotice={currencyNotice}
          />
        );
      case 'blocked':
        return (
          <BlockedState
            isAdmin={canManage}
            blockedReasonKey={blockedReasonKey}
            isStarting={isStarting}
            onStart={start}
            currencyNotice={currencyNotice}
          />
        );
      default:
        return null;
    }
  };

  if (!shouldRenderSuccessFeeMigration(status)) {
    return null;
  }

  return (
    <SettingsSection
      title={t('settings.subscription.successFeeMigration.title')}
      description={t('settings.subscription.successFeeMigration.description')}
      isLast
    >
      {body()}
    </SettingsSection>
  );
};

export default SuccessFeeMigrationCard;
